import * as IndexDB from "localforage";
import { v4 as uuidV4 } from "uuid";

import { HgIndexedDBData } from "./HgIndexedDBData";
import { IdentityToken } from "./IdentityToken";
import { IFluorineCrypto, IKeyPairEnvelope } from "./crypto";
import { generateStateSigningKey } from "./urlStateHandler";
import { Cache } from "../utils/cache";
import { isServiceWorker } from "../utils/runningContext";
import { CacheForServiceWorker } from "../utils/cacheForServiceWorker";
import { SECURITY_TOKEN_PARAMETER_NAME } from "./authenticator/urlHelpers";
import { isTenancyEnabledForSecurityTokenInCookie } from "../utils/tenancyOverrides";

/**
 * The IndexDB key to store key pair. This is shared with Hg, so don't change its name
 */
const KEY_NAME = "key";

/**
 * The IndexDB key to store the realm awaiting authentication tokens after sign-in
 */
const AUTH_REALM = "auth_realm";

/**
 * IndexDB name to persist information between http redirects. This must be in sync with Hg console hg/client/auth/key_storage.cljs
 */
const DB_NAME = "opc-key-store-v2";

/**
 * The IndexDB key to store the info whether user session is timed out
 */
const IDLE_TIMEOUT = "idle_timeout";

/**
 * The IndexDB key to store the info about domain and tenant to be used by SOUP
 */
const SOUP_PARAMETERS = "soup_parameters";

/**
 * IndexDB version. This must be in sync with Hg
 */
const DB_VERSION = 2;

/**
 * Store name in above IndexDB
 */
const STORE_NAME = "keys";

/**
 * Default TTL for the Service Worker Cache
 */
const SW_CACHE_DEFAULT_TTL = 5;

const indexDB = IndexDB.createInstance({
  driver: IndexDB.INDEXEDDB,
  name: DB_NAME,
  storeName: STORE_NAME,
  version: DB_VERSION,
});

const cache = isServiceWorker() ? new CacheForServiceWorker(SW_CACHE_DEFAULT_TTL) : new Cache();

// Only for unit test
export const _testCacheInstance = () =>
  isServiceWorker() ? new CacheForServiceWorker(SW_CACHE_DEFAULT_TTL) : new Cache();

export function getKey(realm: string) {
  return realm === "default" ? KEY_NAME : `${KEY_NAME}_${realm}`;
}

/**
 * Initializes HgIndexedDBData and saves it to IndexDb
 * @returns Promise of HgIndexedDBData
 */
export async function initHgCompatibleDbData(
  realm: string,
  cryptoObject: IFluorineCrypto<CryptoKeyPair, IKeyPairEnvelope>,
): Promise<HgIndexedDBData> {
  const nonce = uuidV4();
  const envelope = await cryptoObject.generateKeyPair();
  const stateSigningKey = await generateStateSigningKey();

  // Save envelop to IndexDB. When SOUP calls back, the envelope's private key is used to sign http requests.
  // Notice though only private key is needed later, we save public key as well, as existing Hg console does.
  const hgCompatibleDbData = {} as HgIndexedDBData;
  hgCompatibleDbData.createdAt = Math.ceil(new Date(Date.now()).getTime());
  hgCompatibleDbData.id = getKey(realm);
  hgCompatibleDbData.private = envelope.materials.privateKey;
  hgCompatibleDbData.public = envelope.materials.publicKey;
  hgCompatibleDbData.nonce = nonce;
  hgCompatibleDbData.state = stateSigningKey;
  await setItem(realm, hgCompatibleDbData);

  return hgCompatibleDbData;
}

/**
 * Returns true if the provided HgIndexedDBData is not undefined and has values for the private,
 * public, and security_token properties
 * @param hgCompatibleDbData
 * @param securityTokenInCookie whitelist flag for moving security token to cookie
 */
export function isHgCompatibleDbDataPresent(
  hgCompatibleDbData: HgIndexedDBData,
  realm: string | undefined,
  securityTokenInCookieTenancyOverrides: string[],
): boolean {
  const isSecurityTokenAvailable: boolean = isTenancyEnabledForSecurityTokenInCookie(
    hgCompatibleDbData?.identity_token?.tenant,
    securityTokenInCookieTenancyOverrides,
  )
    ? !!(realm && getSecurityTokenFromCookie(realm))
    : !!hgCompatibleDbData?.security_token;
  return hgCompatibleDbData && !!hgCompatibleDbData.private && !!hgCompatibleDbData.public && isSecurityTokenAvailable;
}

/**
 * Persist security_token and identity_token on hgCompatibleDbData object
 * @param realm realm use to create key to save token
 * @param securityToken security token to be persisted
 * @param identityToken identity token to be persisted
 * @param securityTokenInCookie whitelist flag for moving security token to cookie
 */
export async function saveTokens(
  realm: string,
  securityToken: string,
  identityToken: IdentityToken,
  securityTokenInCookieTenancyOverrides: string[],
) {
  const key = getKey(realm);
  const hgCompatibleDbData = (await indexDB.getItem(key)) as HgIndexedDBData;
  const hgCompatibleDbDataClone: HgIndexedDBData = {
    ...hgCompatibleDbData,
    ...(!isTenancyEnabledForSecurityTokenInCookie(identityToken?.tenant, securityTokenInCookieTenancyOverrides) && {
      security_token: securityToken,
    }),
    identity_token: identityToken,
  };
  await setItem(realm, hgCompatibleDbDataClone);

  // Saving security token in cookie
  if (isTenancyEnabledForSecurityTokenInCookie(identityToken?.tenant, securityTokenInCookieTenancyOverrides)) {
    const securityTokenCookieName = `${SECURITY_TOKEN_PARAMETER_NAME}-${realm}`;
    document.cookie = `${securityTokenCookieName}=${securityToken}; SameSite=Strict; Secure`;
  }
}
/**
 * Retreives the security token from the cookie.
 * @param realm realm used to create cookie name
 * @returns Security token string
 */
export function getSecurityTokenFromCookie(realm: string): string | undefined {
  const cookieName = `${SECURITY_TOKEN_PARAMETER_NAME}-${realm}`;
  const cookies = document.cookie.split("; ");
  for (const cookie of cookies) {
    const [name, value] = cookie.split("=");
    if (name === cookieName) {
      return value;
    }
  }
  return undefined;
}

/**
 * This function fetches the security token from Session cookie or Indexed DB based on the whitelist flag
 * @param securityTokenInCookie Whitelist flag which decides if security token is in cookie or indexedDB
 * @param hgCompatibleDbData Indexed db data
 * @returns security token string
 */
export function fetchSecurityTokenBasedOnWhitelist(
  hgCompatibleDbData: HgIndexedDBData,
  realm: string,
  securityTokenInCookieTenancyOverrides: string[],
): string {
  return isTenancyEnabledForSecurityTokenInCookie(
    hgCompatibleDbData?.identity_token?.tenant,
    securityTokenInCookieTenancyOverrides,
  )
    ? getSecurityTokenFromCookie(realm)
    : hgCompatibleDbData.security_token;
}

/**
 * Retrieve HgIndexedDBData from IndexedDB
 * @returns Promise of HgIndexedDBData
 */
export async function getHgCompatibleDbData(realm: string): Promise<HgIndexedDBData> {
  const key = getKey(realm);
  if (cache.has(key)) {
    return cache.get(key);
  }
  const data = (await indexDB.getItem(key)) as HgIndexedDBData;
  cache.set(key, data);
  return data;
}

/**
 * Save HgIndexedDBData to Db
 * @param realm realm use to associate the data to
 * @param hgCompatibleDbData data to be saved
 */
export function setItem(realm: string, hgCompatibleDbData: HgIndexedDBData): Promise<HgIndexedDBData> {
  const key = getKey(realm);
  cache.delete(key);
  return indexDB.setItem(key, hgCompatibleDbData);
}

/**
 * Clear Db
 * @param securityTokenInCookie whitelist flag for moving security token to cookie
 * @param realm realm to be cleared
 */
export async function clearDb(
  securityTokenInCookieTenancyOverrides: string[],
  hgCompatibleDbData: HgIndexedDBData,
  realm?: string,
): Promise<void> {
  if (realm) {
    const key = getKey(realm);
    cache.delete(key);
    await indexDB.removeItem(key);
    if (
      isTenancyEnabledForSecurityTokenInCookie(
        hgCompatibleDbData?.identity_token?.tenant,
        securityTokenInCookieTenancyOverrides,
      )
    ) {
      const securityTokenCookieName = `${SECURITY_TOKEN_PARAMETER_NAME}-${realm}`;
      document.cookie = securityTokenCookieName + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;";
    }
  } else {
    cache.clear();
    await indexDB.clear();
  }
}

export function clearSessionCookie(realm: string) {
  const securityTokenCookieName = `${SECURITY_TOKEN_PARAMETER_NAME}-${realm}`;
  document.cookie = securityTokenCookieName + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;";
}

/**
 * Set Authenticating Realm in Db
 * @param realm realm being authenticated
 */
export async function setAuthenticatingRealm(realm: string): Promise<void> {
  await indexDB.setItem(AUTH_REALM, realm);
}

/**
 * Get the realm which is being authenticated
 */
export async function getAuthenticatingRealm(): Promise<string | undefined> {
  const realm: string = await indexDB.getItem(AUTH_REALM);
  return realm;
}

/**
 * Remove placeholder which indicates the realm being authenticated
 */
export async function clearAuthenticatingRealm(): Promise<void> {
  await indexDB.removeItem(AUTH_REALM);
}

/**
 * Set the information about user session timed out
 * @param isSessionTimeout whether session timed out or not
 */
export async function setIdleTimeout(isSessionTimeout: boolean): Promise<void> {
  await indexDB.setItem(IDLE_TIMEOUT, isSessionTimeout);
}

/**
 * Get the information about whether user session is timed out
 */
export async function getIdleTimeout(): Promise<boolean | undefined> {
  return await indexDB.getItem(IDLE_TIMEOUT);
}

/**
 * Remove placeholder for user session timed out
 */
export async function clearIdleTimeout(): Promise<void> {
  await indexDB.removeItem(IDLE_TIMEOUT);
}

/**
 * Remove placeholder for soup parameters used during session timeout
 */
export async function clearSoupParameters(): Promise<void> {
  await indexDB.removeItem(SOUP_PARAMETERS);
}
